'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { ChevronDownGlyph, CloseGlyph } from '../../../internal/icons.js';
import { Button } from '../../actions/button/index.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, } from '../../overlays/dropdown-menu/index.js';
export const defaultBulkActionBarMessages = {
    selected: (count, total) => total !== undefined ? `${count} of ${total} selected` : `${count} selected`,
    cleared: 'Selection cleared',
    clear: 'Clear selection',
    more: 'More',
    label: 'Bulk actions',
};
const useLayoutEffectSafe = typeof window === 'undefined' ? useEffect : useLayoutEffect;
const TEXT_ENTRY = 'input:not([type="checkbox"]):not([type="radio"]),textarea,select,[contenteditable="true"]';
/**
 * Toolbar that appears while items are selected: the count, actions with an
 * overflow menu, and "Clear selection". The count is announced politely.
 * Arrow keys move between its controls (one tab stop).
 */
export const BulkActionBar = forwardRef(function BulkActionBar({ count, totalCount, onClear, actions = [], maxVisibleActions = 3, children, placement = 'inline', busy = false, returnFocusRef, messages, className, ...rest }, ref) {
    const m = { ...defaultBulkActionBarMessages, ...messages };
    const rootRef = useRef(null);
    const toolbarRef = useRef(null);
    const [pending, setPending] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [announcement, setAnnouncement] = useState('');
    const previous = useRef(count);
    const setRoot = useCallback((el) => {
        rootRef.current = el;
        if (typeof ref === 'function')
            ref(el);
        else if (ref)
            ref.current = el;
    }, [ref]);
    // Announce changes to the count (not the first render).
    useEffect(() => {
        if (previous.current === count)
            return;
        previous.current = count;
        setAnnouncement(count > 0 ? m.selected(count, totalCount) : m.cleared);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, totalCount]);
    const blocked = busy || pending !== null;
    const visible = actions.slice(0, maxVisibleActions);
    const overflow = actions.slice(maxVisibleActions);
    const run = (a) => {
        if (blocked || a.disabled)
            return;
        const result = a.onSelect();
        if (result && typeof result.then === 'function') {
            setPending(a.id);
            result.then(() => setPending(null), () => setPending(null));
        }
    };
    // Roving tabindex across the toolbar's controls.
    const items = () => toolbarRef.current
        ? Array.from(toolbarRef.current.querySelectorAll('button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),[role="combobox"]')).filter((el) => !el.closest('[aria-hidden="true"]'))
        : [];
    useLayoutEffectSafe(() => {
        const list = items();
        const i = Math.min(activeIndex, list.length - 1);
        list.forEach((el, n) => {
            el.tabIndex = n === i ? 0 : -1;
        });
    });
    const onKeyDown = (e) => {
        const t = e.target;
        if (t.matches(TEXT_ENTRY))
            return;
        const list = items();
        const i = list.indexOf(t);
        if (i === -1)
            return;
        const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
        const fwd = rtl ? 'ArrowLeft' : 'ArrowRight';
        const back = rtl ? 'ArrowRight' : 'ArrowLeft';
        let next = -1;
        if (e.key === fwd)
            next = (i + 1) % list.length;
        else if (e.key === back)
            next = (i - 1 + list.length) % list.length;
        else if (e.key === 'Home')
            next = 0;
        else if (e.key === 'End')
            next = list.length - 1;
        if (next === -1)
            return;
        e.preventDefault();
        setActiveIndex(next);
        list[next]?.focus();
    };
    const clear = () => {
        onClear();
        setActiveIndex(0);
        const target = returnFocusRef?.current ?? rootRef.current;
        target?.focus();
    };
    const open = count > 0;
    return (_jsxs("div", { ref: setRoot, tabIndex: -1, ...rest, className: cx('mn-bulk-action-bar', className), "data-state": open ? 'open' : 'closed', "data-placement": placement, children: [_jsx("span", { className: "mn-visually-hidden", role: "status", children: announcement }), open ? (_jsxs("div", { ref: toolbarRef, role: "toolbar", "aria-label": m.label, "aria-busy": blocked || undefined, className: "mn-bulk-action-bar__bar", onKeyDown: onKeyDown, onFocus: (e) => {
                    const i = items().indexOf(e.target);
                    if (i !== -1 && i !== activeIndex)
                        setActiveIndex(i);
                }, children: [_jsx("span", { className: "mn-bulk-action-bar__count", children: m.selected(count, totalCount) }), _jsxs("div", { className: "mn-bulk-action-bar__actions", children: [visible.map((a, i) => (_jsx(Button, { size: "sm", variant: a.tone === 'danger' ? 'danger-subtle' : 'secondary', leadingIcon: a.icon, loading: pending === a.id, disabled: a.disabled || (blocked && pending !== a.id), "data-rank": i, className: "mn-bulk-action-bar__action", onClick: () => run(a), children: a.label }, a.id))), overflow.length ? (_jsxs(DropdownMenu, { children: [_jsx(DropdownMenuTrigger, { asChild: true, children: _jsx(Button, { size: "sm", variant: "secondary", disabled: blocked, trailingIcon: _jsx(ChevronDownGlyph, {}), children: m.more }) }), _jsx(DropdownMenuContent, { align: "end", children: overflow.map((a) => (_jsx(DropdownMenuItem, { icon: a.icon, ...(a.tone ? { tone: a.tone } : {}), disabled: a.disabled || blocked, onSelect: () => run(a), children: a.label }, a.id))) })] })) : null, children] }), _jsx(Button, { size: "sm", variant: "ghost", className: "mn-bulk-action-bar__clear", leadingIcon: _jsx(CloseGlyph, {}), onClick: clear, children: m.clear })] })) : null] }));
});
//# sourceMappingURL=bulk-action-bar.js.map